import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import VerwendungenModal, { DrilldownButton } from '../components/VerwendungenModal';

function fmt(h) {
  if (!h && h !== 0) return '–';
  const v = parseFloat(h);
  if (isNaN(v)) return '–';
  const hh = Math.floor(v);
  const mm = Math.round((v - hh) * 60);
  return `${hh}h ${mm.toString().padStart(2, '0')}m`;
}

function datumDe(d) {
  if (!d) return '–';
  return new Date(d).toLocaleDateString('de-DE');
}

function fmtEur(n) {
  if (!n && n !== 0) return '–';
  return parseFloat(n).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}
function fmtMenge(n) {
  if (n == null || n === '') return '0';
  const v = parseFloat(n);
  if (isNaN(v)) return '0';
  return v.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 3 });
}
// Stunden als „12h 30m" — gleiche Optik wie Projektfortschritt + alte
// `fmt()` Funktion oben.
function fmtStunden(h) {
  if (h == null || h === '') return '0h';
  const v = parseFloat(h);
  if (isNaN(v) || v === 0) return '0h';
  const hh = Math.floor(v);
  const mm = Math.round((v - hh) * 60);
  return `${hh}h ${mm.toString().padStart(2, '0')}m`;
}

// A.6.3: Reihenfolge der Typen + Labels fuer die Typ-Aufteilung im
// detail-Sub-Block. Muss mit Backend (computeStundenAggregate) in Sync
// bleiben. Bewusste Mini-Duplikation zu Projektfortschritt - utils-
// Refactor folgt separat, wenn mehrere Stellen die gleichen Helper
// brauchen.
const TYP_REIHENFOLGE_BA = [
  ['regulaer', 'Regulär'],
  ['regie',    'Regie'],
  ['zusatz',   'Zusatz'],
];

// G.4: deutsche Labels fuer Status-Filter, einheitlich genutzt in Info-Card
// und Excel-Uebersicht. Fallback "Alle" bei unbekanntem/leerem Wert
// (alte Backend-Response ohne filter-Block).
function statusLabel(s) {
  if (s === 'fertig') return 'Nur fertig';
  if (s === 'entwurf') return 'Nur Entwurf';
  return 'Alle';
}

// G.3: primärer Stunden-Block in der Auswertung (dokumentierte Leistung im
// Tagesbericht). Im Gegensatz zu Projektfortschritt zeitraum-orientiert.
// Eigene Komponente, nicht mit Projektfortschritt geteilt (anderes
// Aggregat-Konzept).
// G.6: zur Nachweis-Sektion „Bautagebücher im Zeitraum" aufgewertet — die
// Tabelle belegt, aus welchen BTs die Stunden- UND Material-Summen des
// Zeitraums stammen (material_zeilen = Count manueller Material-Zeilen,
// Auto gefiltert; bewusst keine Mengensumme über gemischte Einheiten).
function BautagebuchStundenSection({ stunden, navigate }) {
  const liste = stunden?.liste || [];
  const gesamt = parseFloat(stunden?.gesamt) || 0;
  const tage = stunden?.arbeitstage ?? 0;
  const anzahl = stunden?.anzahl_bautagebuecher ?? 0;

  if (liste.length === 0) {
    return (
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-head">
          <div className="card-title">Bautagebücher im Zeitraum · 0h</div>
        </div>
        <div style={{ padding: '14px 0', fontSize: 13, color: 'var(--text-3)', textAlign: 'center' }}>
          Im gewählten Zeitraum gibt es keine Bautagebuch-Einträge auf dieser Baustelle.
        </div>
      </div>
    );
  }

  return (
    <div className="card" style={{ marginBottom: 14 }}>
      <div className="card-head">
        <div className="card-title">
          Bautagebücher im Zeitraum · {fmtStunden(gesamt)}
          <span style={{ marginLeft: 8, color: 'var(--text-3)', fontWeight: 400, fontSize: 12 }}>
            aus {anzahl} {anzahl === 1 ? 'Bericht' : 'Berichten'}
            {tage > 0 && ` · ${tage} ${tage === 1 ? 'Arbeitstag' : 'Arbeitstage'}`}
          </span>
        </div>
      </div>

      <div style={{
        background: 'var(--bg-elevated)', color: 'var(--text-3)',
        padding: '8px 12px', borderRadius: 'var(--radius)',
        fontSize: 12, marginBottom: 10, lineHeight: 1.5,
      }}>
        Nachweis: Diese Bautagebücher liegen den Stunden- und Material-Summen
        des Zeitraums zugrunde. Stunden = dokumentierte Leistung (primäre
        Quelle für Rechnung und Leistungsnachweis); Zeiterfassung
        (Anwesenheit) wird unten separat als Abgleich angezeigt.
      </div>

      {/* A.6.3: Detailaggregate aus bautagebuch_stunden im Zeitraum
          (Pro-Person). Analog zu A.6.2 in Projektfortschritt, aber
          zeitraum-gefiltert. Robust gegen fehlendes detail: rendert
          gar nicht, wenn das Backend (alt) keinen detail-Block liefert
          oder im Zeitraum keine Stunden vorhanden sind. Snapshots
          strikt getrennt, kein Aufaddieren, "Keine Rechnung"-Hinweis. */}
      {(() => {
        const detail = stunden?.detail;
        if (!detail) return null;
        const gesamtNum = Number(detail.gesamt?.stunden) || 0;
        if (gesamtNum <= 0) return null;

        const personenGesamt = detail.personen?.gesamt ?? 0;
        const externZeilen = detail.personen?.extern_zeilen ?? 0;
        // A.7 additiv, robust gegen altes Backend ohne die neuen Felder.
        const subEindeutig = detail.personen?.subunternehmer_eindeutig ?? 0;

        const typItems = TYP_REIHENFOLGE_BA
          .map(([k, label]) => ({ key: k, label, sum: Number(detail.by_typ?.[k]) || 0 }))
          .filter(x => x.sum > 0);

        const abrJa = Number(detail.by_abrechenbar?.ja) || 0;
        const abrNein = Number(detail.by_abrechenbar?.nein) || 0;

        const rollen = Array.isArray(detail.by_rolle)
          ? detail.by_rolle.filter(r => (Number(r.stunden) || 0) > 0)
          : [];

        const intern = detail.bewertung?.intern;
        const lv     = detail.bewertung?.lv;
        const internRelevant = intern && (
          (Number(intern.bewertete_stunden) || 0) > 0 ||
          (Number(intern.nicht_bewertbar_stunden) || 0) > 0
        );
        const lvRelevant = lv && (
          (Number(lv.bewertete_stunden) || 0) > 0 ||
          (Number(lv.nicht_bewertbar_stunden) || 0) > 0
        );

        const renderBewertung = (block, label) => {
          const bewertet = Number(block.bewertete_stunden) || 0;
          const nb = Number(block.nicht_bewertbar_stunden) || 0;
          const mig = Number(block.nicht_bewertbar_aus_migration_stunden) || 0;
          return (
            <div style={{ marginTop: 2 }}>
              <span style={{ color: 'var(--text-3)' }}>{label}: </span>
              <span style={{ fontWeight: 500 }}>{fmtEur(block.wert)}</span>
              <span style={{ color: 'var(--text-3)', fontSize: 11 }}>
                {' '}({fmtStunden(bewertet)} bewertet
                {nb > 0 && (
                  <>
                    , {fmtStunden(nb)} nicht bewertbar
                    {mig > 0 && (
                      <> – davon {fmtStunden(mig)} aus alter Sammelerfassung</>
                    )}
                  </>
                )})
              </span>
            </div>
          );
        };

        return (
          <div style={{
            background: 'var(--bg-elevated)',
            borderRadius: 'var(--radius)',
            padding: '10px 12px',
            marginBottom: 10,
            fontSize: 12,
            lineHeight: 1.55,
          }}>
            <div>
              <span style={{ color: 'var(--text-3)' }}>Personen: </span>
              <span style={{ fontWeight: 600 }}>{personenGesamt}</span>
              {externZeilen > 0 && (
                <span style={{ color: 'var(--text-3)' }}>
                  {' '}(davon {externZeilen}{' '}
                  {externZeilen === 1 ? 'externe Zeile' : 'externe Zeilen'}
                  {subEindeutig > 0 && <> · {subEindeutig} Subunternehmer eindeutig</>})
                </span>
              )}
            </div>

            {typItems.length > 0 && (
              <div style={{ marginTop: 4 }}>
                <span style={{ color: 'var(--text-3)' }}>Typ: </span>
                {typItems.map((x, i) => (
                  <span key={x.key}>
                    {i > 0 && <span style={{ color: 'var(--text-3)' }}> · </span>}
                    {fmtStunden(x.sum)} {x.label}
                  </span>
                ))}
              </div>
            )}

            {(abrJa > 0 || abrNein > 0) && (
              <div style={{ marginTop: 4 }}>
                <span style={{ color: 'var(--text-3)' }}>Abrechenbar: </span>
                {abrJa > 0 && <span>{fmtStunden(abrJa)} ja</span>}
                {abrJa > 0 && abrNein > 0 && (
                  <span style={{ color: 'var(--text-3)' }}> · </span>
                )}
                {abrNein > 0 && <span>{fmtStunden(abrNein)} nein</span>}
              </div>
            )}

            {rollen.length > 1 && (
              <div style={{ marginTop: 4 }}>
                <span style={{ color: 'var(--text-3)' }}>Funktion: </span>
                {rollen.map((r, i) => (
                  <span key={r.rolle_id ?? `r-${i}`}>
                    {i > 0 && <span style={{ color: 'var(--text-3)' }}> · </span>}
                    {fmtStunden(r.stunden)} {r.rolle_bezeichnung || '–'}
                  </span>
                ))}
              </div>
            )}

            {(internRelevant || lvRelevant) && (
              <div style={{
                marginTop: 8, paddingTop: 8,
                borderTop: '1px solid var(--border)',
              }}>
                {internRelevant && renderBewertung(intern, 'Interne Kosten (Snapshot)')}
                {lvRelevant && renderBewertung(lv, 'Leistungsnachweis (Snapshot)')}
                <div style={{
                  marginTop: 6, fontSize: 11,
                  color: 'var(--text-3)', fontStyle: 'italic',
                }}>
                  Aus Stundensätzen zum Buchungszeitpunkt. Keine Rechnung.
                </div>
              </div>
            )}
          </div>
        );
      })()}

      <div className="table-wrap table-mobile-stack">
        <table>
          <thead>
            <tr>
              <th>Datum</th>
              <th>Bautagebuch</th>
              <th>Std/Tag</th>
              <th>Monteure</th>
              <th>Mannstunden</th>
              <th>Material</th>
              <th>Bauleiter</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {liste.map(b => {
              const statusBadge = b.status === 'fertig'
                ? <span className="badge badge-green">Fertig</span>
                : <span className="badge badge-grey">Entwurf</span>;
              return (
                <tr key={b.id}>
                  <td data-label="Datum" className="text-mono" style={{ whiteSpace: 'nowrap' }}>
                    {datumDe(b.datum)}
                  </td>
                  <td data-label="" className="td-title" style={{ fontWeight: 500 }}>
                    #{b.id}
                  </td>
                  <td data-label="Std/Tag" className="text-mono">
                    {b.std_gesamt != null ? fmtMenge(b.std_gesamt) + ' h' : '–'}
                  </td>
                  <td data-label="Monteure" className="text-mono" style={{ textAlign: 'center' }}>
                    {b.anzahl_monteure ?? '–'}
                  </td>
                  <td data-label="Mannstunden" className="text-mono" style={{ fontWeight: 600, color: 'var(--copper)' }}>
                    {fmtStunden(b.gesamtstunden)}
                  </td>
                  <td data-label="Material" className="text-mono">
                    {(b.material_zeilen ?? 0) > 0
                      ? `${b.material_zeilen} ${b.material_zeilen === 1 ? 'Zeile' : 'Zeilen'}`
                      : '–'}
                  </td>
                  <td data-label="Bauleiter">{b.bauleiter || '–'}</td>
                  <td data-label="Status">{statusBadge}</td>
                  <td data-label="" className="td-actions">
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => navigate(`/bautagebuch/${b.id}`)}
                      title="Bautagebuch öffnen"
                    >→ Öffnen</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// G.1: Eine Material-Tabelle pro Gruppe (Hauptauftrag/Nachträge/Frei).
// hideValueColumns=true → ohne Einheitspreis/Wert-Spalten (für „Frei",
// weil ohne LV-Position keine seriöse Preisbasis).
// Mobile-Stacking via table-mobile-stack + data-label, ProgressBar-frei.
function MaterialGruppeTabelle({ titel, items, hideValueColumns, summeWert, hinweis, onDrilldown }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="card" style={{ marginBottom: 14 }}>
      <div className="card-head">
        <div className="card-title">
          {titel} · {items.length}
          {!hideValueColumns && summeWert != null && (
            <span style={{ marginLeft: 8, color: 'var(--text-3)', fontWeight: 400, fontSize: 12 }}>
              · Summe: {fmtEur(summeWert)}
            </span>
          )}
        </div>
      </div>
      {hinweis && (
        <div style={{
          background: 'var(--bg-elevated)', color: 'var(--text-3)',
          padding: '8px 12px', borderRadius: 'var(--radius)',
          fontSize: 12, marginBottom: 10, lineHeight: 1.5,
        }}>{hinweis}</div>
      )}
      <div className="table-wrap table-mobile-stack">
        <table>
          <thead>
            <tr>
              <th>Pos.-Nr.</th>
              <th>Bezeichnung</th>
              <th>Einheit</th>
              <th>Menge</th>
              {!hideValueColumns && <th>Einheitspreis</th>}
              {!hideValueColumns && <th>Wert</th>}
              <th>Verwendungen</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((m, i) => (
              <tr key={m.projekt_position_id || `frei-${i}`}>
                <td data-label="Pos.-Nr." className="text-mono text-copper" style={{ fontWeight: 600 }}>
                  {m.pos_nr || '–'}
                </td>
                <td data-label="" className="td-title" style={{ fontWeight: 500 }}>
                  {m.bezeichnung || '–'}
                </td>
                <td data-label="Einheit" style={{ textAlign: 'center' }}>{m.einheit || '–'}</td>
                <td data-label="Menge" className="text-mono" style={{ fontWeight: 600 }}>
                  {fmtMenge(m.menge)}
                </td>
                {!hideValueColumns && (
                  <td data-label="Einheitspreis" className="text-mono" style={{ color: 'var(--text-3)' }}>
                    {m.einheitspreis != null ? fmtEur(m.einheitspreis) : '–'}
                  </td>
                )}
                {!hideValueColumns && (
                  <td data-label="Wert" className="text-mono" style={{ fontWeight: 600, color: 'var(--copper)' }}>
                    {m.wert != null ? fmtEur(m.wert) : '–'}
                  </td>
                )}
                <td data-label="Verwendungen" className="text-mono" style={{ color: 'var(--text-3)' }}>
                  {m.verwendungen} {m.verwendungen === 1 ? 'BT' : 'BTs'}
                </td>
                <td data-label="" className="td-actions">
                  {onDrilldown && (
                    <DrilldownButton
                      onClick={() => onDrilldown(m)}
                      title="Buchungen im Zeitraum ansehen"
                    />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Lädt SheetJS bei Bedarf per <script>-Tag und liefert window.XLSX.
// Vorher per `await import('https://cdn.jsdelivr.net/...')` — das ist ein URL-Import,
// den webpack 5 (CRA 5) ohne `output.module: true` nicht bauen kann. Klassisches
// Script-Loading umgeht den Build-Fehler ohne neue Dependency und lädt erst beim Klick.
function loadXLSX() {
  return new Promise((resolve, reject) => {
    if (window.XLSX) return resolve(window.XLSX);
    const existing = document.querySelector('script[data-xlsx-cdn]');
    if (existing) {
      existing.addEventListener('load', () => window.XLSX ? resolve(window.XLSX) : reject(new Error('XLSX nicht verfügbar')));
      existing.addEventListener('error', () => reject(new Error('XLSX-Skript konnte nicht geladen werden')));
      return;
    }
    const s = document.createElement('script');
    s.src = 'https://cdn.jsdelivr.net/npm/xlsx@0.18.5/dist/xlsx.full.min.js';
    s.async = true;
    s.dataset.xlsxCdn = '1';
    s.onload = () => window.XLSX ? resolve(window.XLSX) : reject(new Error('XLSX nicht verfügbar'));
    s.onerror = () => reject(new Error('XLSX-Skript konnte nicht geladen werden'));
    document.head.appendChild(s);
  });
}

export default function BaustellenAuswertung() {
  const { user } = useAuth() || {};
  const navigate = useNavigate();

  const [baustellen, setBaustellen] = useState([]);
  const [baustelleId, setBaustelleId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const heute = new Date().toISOString().split('T')[0];
  const vorMonat = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  const [vonDatum, setVonDatum] = useState(vorMonat);
  const [bisDatum, setBisDatum] = useState(heute);
  // G.4: Default 'fertig' - Auswertung ist Grundlage fuer Rechnung/Leistungs-
  // nachweis, Entwuerfe sollen standardmaessig nicht enthalten sein.
  const [statusFilter, setStatusFilter] = useState('fertig');

  // G.2: Drilldown-Modal — gleiche Komponente wie Projektfortschritt,
  // aber mit Zeitraum (von/bis) durchgereicht, damit der Modal nur die
  // Verwendungen im aktuellen Auswertungs-Zeitraum zeigt.
  const [drilldown, setDrilldown] = useState(null);
  const openLvDrill = useCallback((m) => {
    // m hat projekt_position_id (LV-zugeordnet) — analog zu Projektfortschritt
    setDrilldown({
      kind: 'position',
      position: { id: m.projekt_position_id, pos_nr: m.pos_nr, bezeichnung: m.bezeichnung, einheit: m.einheit, sollmenge: null, menge: m.menge, verbraucht_menge: m.menge },
    });
  }, []);
  const openFreiDrill = useCallback((m) => {
    setDrilldown({
      kind: 'frei',
      item: { pos_nr: m.pos_nr, bezeichnung: m.bezeichnung, einheit: m.einheit, menge_gesamt: m.menge, verwendungen: m.verwendungen },
      baustelleId,
    });
  }, [baustelleId]);
  const closeDrill = useCallback(() => setDrilldown(null), []);
  // Wenn Baustelle oder Zeitraum wechselt: offenes Modal schließen, sonst
  // verweist es auf veraltete Daten.
  useEffect(() => { setDrilldown(null); }, [baustelleId, vonDatum, bisDatum]);

  useEffect(() => {
    if (user?.role !== 'admin') { navigate('/'); return; }
    api.get('/baustellen').then(r => setBaustellen(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [user, navigate]);

  const laden = async () => {
    if (!baustelleId) return;
    setLoading(true);
    setData(null);
    try {
      const r = await api.get(`/auswertung/baustelle/${baustelleId}?von=${vonDatum}&bis=${bisDatum}&status=${statusFilter}`);
      setData(r.data);
    } catch {
      alert('Fehler beim Laden der Auswertung');
    }
    setLoading(false);
  };

  const exportPdf = () => {
    if (!baustelleId) return;
    const token = localStorage.getItem('token');
    window.open(`/api/auswertung/baustelle/${baustelleId}/pdf?von=${vonDatum}&bis=${bisDatum}&status=${statusFilter}&token=${token}`, '_blank');
  };

  const exportExcel = async () => {
    if (!data) return;
    try {
      const XLSX = await loadXLSX();
      const wb = XLSX.utils.book_new();

      // G.1: strukturierte Material-Daten
      const matHaupt = data.material?.hauptauftrag?.items || [];
      const matNach  = data.material?.nachtraege?.items  || [];
      const matFrei  = data.material?.frei?.items        || [];
      const matAnz   = matHaupt.length + matNach.length + matFrei.length;

      // G.3: Bautagebuch-Stunden = primaere Quelle
      const btL = data.stunden_bautagebuch?.liste || [];
      const btGesamt = parseFloat(data.stunden_bautagebuch?.gesamt || 0);
      const btAnz = data.stunden_bautagebuch?.anzahl_bautagebuecher || 0;
      const btTage = data.stunden_bautagebuch?.arbeitstage || 0;

      // Deckblatt — BT primär oben, ZE sekundär darunter, Material wie G.1
      const deckblatt = [
        ['DV Elektro — Baustellen-Auswertung'],
        [],
        ['Baustelle:', data.baustelle.name],
        ['Adresse:', [data.baustelle.adresse, data.baustelle.plz, data.baustelle.ort].filter(Boolean).join(' ')],
        ['Zeitraum:', `${datumDe(vonDatum)} – ${datumDe(bisDatum)}`],
        // G.4: tatsaechlich angewandter Filter aus data.filter.status,
        // damit das Excel zur angezeigten Auswertung passt (nicht zum
        // potentiell schon weitergedrehten Select-State).
        ['Filter Status:', statusLabel(data.filter?.status)],
        ['Erstellt am:', datumDe(heute)],
        [],
        ['Stunden (Bautagebuch — dokumentierte Leistung):', btGesamt.toFixed(2) + ' h'],
        ['  Anzahl Bautagebücher:', btAnz],
        ['  Arbeitstage:', btTage],
        [],
        ['Stunden (Zeiterfassung — Anwesenheit, Abgleich):', parseFloat(data.gesamt_stunden || 0).toFixed(2) + ' h'],
        ['  Einträge:', data.zeiteintraege?.length || 0],
        ['  Mitarbeiter:', data.mitarbeiter_anzahl || 0],
        [],
        ['Material-Positionen gesamt:', matAnz],
        ['  davon Hauptauftrag:', matHaupt.length],
        ['  davon Nachträge:', matNach.length],
        ['  davon nicht zugeordnet:', matFrei.length],
        [],
        ['Material-Wert Hauptauftrag:', parseFloat(data.material?.hauptauftrag?.summe_wert || 0)],
        ['Material-Wert Nachträge:',    parseFloat(data.material?.nachtraege?.summe_wert  || 0)],
        ['Material-Wert Gesamt (LV):',  parseFloat(data.material?.summe_wert_gesamt       || 0)],
      ];
      const ws1 = XLSX.utils.aoa_to_sheet(deckblatt);
      ws1['!cols'] = [{ wch: 44 }, { wch: 40 }];
      XLSX.utils.book_append_sheet(wb, ws1, 'Übersicht');

      // G.3: Bautagebuch-Stunden Sheet (PRIMÄR)
      if (btL.length > 0) {
        const btHeader = ['Datum', 'Bautagebuch', 'Std/Tag', 'Monteure', 'Mannstunden', 'Material-Zeilen', 'Bauleiter', 'Status', 'Ersteller'];
        const btRows = btL.map(b => [
          datumDe(b.datum),
          '#' + b.id,
          b.std_gesamt != null ? parseFloat(b.std_gesamt) : '',
          b.anzahl_monteure ?? '',
          parseFloat(b.gesamtstunden || 0),
          b.material_zeilen ?? 0,
          b.bauleiter || '–',
          b.status === 'fertig' ? 'Fertig' : 'Entwurf',
          b.ersteller_name || '–',
        ]);
        btRows.push(['', '', '', 'Summe:', btGesamt, '', '', '', '']);
        const wsBt = XLSX.utils.aoa_to_sheet([btHeader, ...btRows]);
        wsBt['!cols'] = [{ wch: 12 }, { wch: 12 }, { wch: 10 }, { wch: 10 }, { wch: 12 }, { wch: 14 }, { wch: 20 }, { wch: 10 }, { wch: 18 }];
        XLSX.utils.book_append_sheet(wb, wsBt, 'Stunden — Bautagebuch');
      }

      // Zeiterfassung Sheet (SEKUNDÄR — Abgleich)
      if (data.zeiteintraege?.length > 0) {
        const stundenHeader = [['Datum', 'Mitarbeiter', 'Einstempel', 'Ausstempel', 'Stunden', 'Adresse']];
        const stundenRows = data.zeiteintraege.map(z => [
          datumDe(z.datum),
          z.user_name || '–',
          z.einstempel?.substring(0, 5) || '–',
          z.ausstempel?.substring(0, 5) || '–',
          parseFloat(z.stunden || 0),
          z.adresse_ein || '–',
        ]);
        stundenRows.push(['', '', '', 'Gesamt:', parseFloat(data.gesamt_stunden || 0), '']);
        const ws2 = XLSX.utils.aoa_to_sheet([...stundenHeader, ...stundenRows]);
        ws2['!cols'] = [{ wch: 12 }, { wch: 20 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 35 }];
        XLSX.utils.book_append_sheet(wb, ws2, 'Anwesenheit (ZE)');
      }

      // G.1: drei separate Material-Sheets statt einem,
      // damit Abrechnung/Nachträge/nicht-zugeordnet sauber getrennt sind.
      const lvHeader = ['Pos.-Nr.', 'Bezeichnung', 'Einheit', 'Menge', 'Einheitspreis', 'Wert', 'Verwendungen'];
      const lvCols   = [{ wch: 14 }, { wch: 36 }, { wch: 10 }, { wch: 12 }, { wch: 14 }, { wch: 14 }, { wch: 14 }];
      const lvRow = (m) => [
        m.pos_nr || '–', m.bezeichnung || '–', m.einheit || '–',
        parseFloat(m.menge || 0),
        m.einheitspreis != null ? parseFloat(m.einheitspreis) : '',
        m.wert != null ? parseFloat(m.wert) : '',
        m.verwendungen || 0,
      ];
      if (matHaupt.length > 0) {
        const rows = matHaupt.map(lvRow);
        rows.push(['', '', '', '', 'Summe:', parseFloat(data.material.hauptauftrag.summe_wert || 0), '']);
        const ws = XLSX.utils.aoa_to_sheet([lvHeader, ...rows]);
        ws['!cols'] = lvCols;
        XLSX.utils.book_append_sheet(wb, ws, 'Material — Hauptauftrag');
      }
      if (matNach.length > 0) {
        const rows = matNach.map(lvRow);
        rows.push(['', '', '', '', 'Summe:', parseFloat(data.material.nachtraege.summe_wert || 0), '']);
        const ws = XLSX.utils.aoa_to_sheet([lvHeader, ...rows]);
        ws['!cols'] = lvCols;
        XLSX.utils.book_append_sheet(wb, ws, 'Material — Nachträge');
      }
      if (matFrei.length > 0) {
        const freiHeader = ['Pos.-Nr.', 'Bezeichnung', 'Einheit', 'Menge', 'Verwendungen'];
        const freiCols   = [{ wch: 14 }, { wch: 36 }, { wch: 10 }, { wch: 12 }, { wch: 14 }];
        const rows = matFrei.map(m => [
          m.pos_nr || '–', m.bezeichnung || '–', m.einheit || '–',
          parseFloat(m.menge || 0),
          m.verwendungen || 0,
        ]);
        const ws = XLSX.utils.aoa_to_sheet([freiHeader, ...rows]);
        ws['!cols'] = freiCols;
        XLSX.utils.book_append_sheet(wb, ws, 'Material — nicht zugeordnet');
      }

      const baustName = data.baustelle.name.replace(/[^a-z0-9]/gi, '_').substring(0, 20);
      XLSX.writeFile(wb, `Auswertung_${baustName}_${vonDatum}_${bisDatum}.xlsx`);
    } catch (e) {
      console.error(e);
      alert('Excel-Export fehlgeschlagen');
    }
  };

  if (user?.role !== 'admin') return null;

  const bs = baustellen.find(b => b.id === parseInt(baustelleId));

  return (
    <div style={{ paddingBottom: 100 }}>
      <div className="page-header">
        <div>
          <div className="page-title">Baustellen-Auswertung</div>
          <div className="page-sub">Grundlage für Abschlagszahlungen und Rechnungen</div>
        </div>
        {data && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="btn btn-secondary" onClick={exportExcel}>📊 Excel</button>
            <button className="btn btn-primary" onClick={exportPdf}>📄 PDF</button>
          </div>
        )}
      </div>

      {/* Filter */}
      <div style={{ background: 'var(--bg-2)', borderRadius: 12, padding: 16, border: '1px solid var(--border)', marginBottom: 24 }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '2 1 220px' }}>
            <label style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Baustelle</label>
            <select
              value={baustelleId}
              onChange={e => { setBaustelleId(e.target.value); setData(null); }}
              style={{ background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', color: 'var(--text-1)', fontSize: 14, outline: 'none' }}
            >
              <option value="">— Baustelle wählen —</option>
              {baustellen.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '1 1 140px' }}>
            <label style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Von</label>
            <input type="date" value={vonDatum} onChange={e => setVonDatum(e.target.value)}
              style={{ background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', color: 'var(--text-1)', fontSize: 14, outline: 'none' }} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '1 1 140px' }}>
            <label style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Bis</label>
            <input type="date" value={bisDatum} onChange={e => setBisDatum(e.target.value)}
              style={{ background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', color: 'var(--text-1)', fontSize: 14, outline: 'none' }} />
          </div>
          {/* G.4: Status-Filter. Reihenfolge bewusst: 'fertig' (Default,
              Rechnungs-/Nachweis-Basis) → 'alle' (Pruefansicht) → 'entwurf'
              (Spezialfall). Statuswechsel verwirft das aktuelle Ergebnis,
              damit die Stat-Cards nicht stale auf alten Filter zeigen
              (gleiches Pattern wie Baustellenwechsel). */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '1 1 160px' }}>
            <label style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Status</label>
            <select
              value={statusFilter}
              onChange={e => { setStatusFilter(e.target.value); setData(null); }}
              style={{ background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', color: 'var(--text-1)', fontSize: 14, outline: 'none' }}
            >
              <option value="fertig">Nur fertig</option>
              <option value="alle">Alle</option>
              <option value="entwurf">Nur Entwurf</option>
            </select>
          </div>
          <button
            onClick={laden}
            disabled={!baustelleId || loading}
            className="btn btn-primary"
            style={{ alignSelf: 'flex-end', opacity: !baustelleId ? 0.5 : 1 }}
          >
            {loading ? '⏳ Lädt…' : '🔍 Auswerten'}
          </button>
        </div>
      </div>

      {!data && !loading && (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-3)' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📊</div>
          <div style={{ fontSize: 16, marginBottom: 4 }}>Baustelle und Zeitraum wählen</div>
          <div style={{ fontSize: 13 }}>Dann auf „Auswerten" klicken</div>
        </div>
      )}

      {loading && (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-3)' }}>
          <div className="spinner" style={{ margin: '0 auto 12px' }} />
          Wird berechnet…
        </div>
      )}

      {data && (
        <>
          {/* Baustellen-Info */}
          <div className="card card-accent" style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--text-1)', marginBottom: 4 }}>{data.baustelle.name}</div>
                <div style={{ fontSize: 13, color: 'var(--text-3)' }}>
                  {[data.baustelle.adresse, data.baustelle.plz, data.baustelle.ort].filter(Boolean).join(' ')}
                  {data.baustelle.auftraggeber && ` · AG: ${data.baustelle.auftraggeber}`}
                </div>
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-3)', textAlign: 'right' }}>
                <div>Zeitraum:</div>
                <div style={{ fontWeight: 500, color: 'var(--text-2)' }}>{datumDe(vonDatum)} – {datumDe(bisDatum)}</div>
                {/* G.4: aktiv geladener Filter (aus data.filter.status, nicht
                    aus dem Select-State - der kann nach dem Laden bereits
                    weitergedreht sein, ohne dass neu „Auswerten" geklickt wurde). */}
                <div style={{ marginTop: 4 }}>Status: <span style={{ fontWeight: 500, color: 'var(--text-2)' }}>{statusLabel(data.filter?.status)}</span>{data.filter?.status === 'fertig' && <span style={{ color: 'var(--text-3)' }}> · Entwürfe ausgeblendet</span>}</div>
              </div>
            </div>
          </div>

          {/* G.3: Stat-Cards mit klarer Trennung Stunden-Quellen.
              BT (Bautagebuch) = primär (dokumentierte Leistung).
              ZE (Zeiterfassung) = sekundär (Anwesenheit), grauer Akzent.
              Material-Pos./Wert (LV) wie in G.1. */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 20 }}>
            {[
              {
                label: 'Stunden (BT)',
                sub: (() => {
                  const a = data.stunden_bautagebuch?.anzahl_bautagebuecher || 0;
                  const t = data.stunden_bautagebuch?.arbeitstage || 0;
                  if (a === 0) return null;
                  return `${a} ${a === 1 ? 'Bericht' : 'Berichte'} · ${t} ${t === 1 ? 'Tag' : 'Tage'}`;
                })(),
                value: fmtStunden(data.stunden_bautagebuch?.gesamt),
                accent: (parseFloat(data.stunden_bautagebuch?.gesamt) || 0) > 0
                  ? 'var(--copper)' : 'var(--text-3)',
              },
              {
                label: 'Stunden (ZE) — Abgleich',
                sub: (() => {
                  const m = data.mitarbeiter_anzahl || 0;
                  const t = data.arbeitstage || 0;
                  if (m === 0 && t === 0) return null;
                  return `${m} ${m === 1 ? 'Mitarbeiter' : 'Mitarbeiter'} · ${t} ${t === 1 ? 'Tag' : 'Tage'}`;
                })(),
                value: fmt(data.gesamt_stunden),
                accent: 'var(--text-3)',
              },
              {
                label: 'Material-Pos.',
                value: (data.material?.hauptauftrag?.items?.length || 0)
                     + (data.material?.nachtraege?.items?.length || 0)
                     + (data.material?.frei?.items?.length || 0),
              },
              {
                label: 'Wert (LV)',
                value: fmtEur(data.material?.summe_wert_gesamt),
                accent: (parseFloat(data.material?.summe_wert_gesamt) || 0) > 0
                  ? 'var(--copper)' : 'var(--text-3)',
              },
            ].map(k => (
              <div key={k.label} style={{ background: 'var(--bg-2)', borderRadius: 12, padding: 16, border: '1px solid var(--border)', minWidth: 0 }}>
                <div style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{k.label}</div>
                <div style={{ fontSize: 22, fontWeight: 600, color: k.accent || 'var(--copper)', wordBreak: 'break-word' }}>{k.value}</div>
                {k.sub && (
                  <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 6 }}>{k.sub}</div>
                )}
              </div>
            ))}
          </div>

          {/* G.3: PRIMÄRER Stunden-Block — Bautagebuch (dokumentierte
              Leistung). Steht oben, weil im Controlling/Abrechnungs-
              Kontext die Hauptquelle. */}
          <BautagebuchStundenSection
            stunden={data.stunden_bautagebuch}
            navigate={navigate}
          />

          {/* G.3: SEKUNDÄRE Stunden-Blöcke — Zeiterfassung (Anwesenheit)
              als Abgleich. Optisch grauer/zurückhaltender. */}
          {data.stunden_pro_mitarbeiter?.length > 0 && (
            <div style={{ background: 'var(--bg-2)', borderRadius: 12, border: '1px solid var(--border)', marginBottom: 16, overflow: 'hidden' }}>
              <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', fontSize: 12, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Anwesenheit (Zeiterfassung) — Stunden nach Mitarbeiter
              </div>
              <div style={{ padding: '8px 0' }}>
                {data.stunden_pro_mitarbeiter.map((m, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 16px', borderBottom: i < data.stunden_pro_mitarbeiter.length - 1 ? '1px solid var(--border)' : 'none' }}>
                    <div style={{ fontWeight: 500, color: 'var(--text-1)' }}>{m.user_name}</div>
                    <div style={{ fontWeight: 600, color: 'var(--text-2)' }}>{fmt(m.stunden)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Zeiteinträge Detail (Anwesenheit) */}
          {data.zeiteintraege?.length > 0 && (
            <div style={{ background: 'var(--bg-2)', borderRadius: 12, border: '1px solid var(--border)', marginBottom: 16, overflow: 'hidden' }}>
              <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', fontSize: 12, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Anwesenheit (Zeiterfassung) — Einzeleinträge ({data.zeiteintraege.length})
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: 'var(--bg-1)' }}>
                      {['Datum', 'Mitarbeiter', 'Von', 'Bis', 'Stunden'].map(h => (
                        <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 500, color: 'var(--text-3)', fontSize: 11, borderBottom: '1px solid var(--border)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.zeiteintraege.map((z, i) => (
                      <tr key={z.id} style={{ borderBottom: '1px solid var(--border)', background: i % 2 === 0 ? 'transparent' : 'var(--bg-1)' }}>
                        <td style={{ padding: '8px 12px', color: 'var(--text-2)', whiteSpace: 'nowrap' }}>{datumDe(z.datum)}</td>
                        <td style={{ padding: '8px 12px', fontWeight: 500, color: 'var(--text-1)' }}>{z.user_name || '–'}</td>
                        <td style={{ padding: '8px 12px', color: 'var(--text-2)' }}>{z.einstempel?.substring(0, 5) || '–'}</td>
                        <td style={{ padding: '8px 12px', color: 'var(--text-2)' }}>{z.ausstempel?.substring(0, 5) || '–'}</td>
                        <td style={{ padding: '8px 12px', fontWeight: 600, color: 'var(--text-2)' }}>{fmt(z.stunden)}</td>
                      </tr>
                    ))}
                    <tr style={{ background: 'var(--bg-1)', borderTop: '2px solid var(--border)' }}>
                      <td colSpan={4} style={{ padding: '8px 12px', fontWeight: 600, color: 'var(--text-2)', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>Gesamt (Anwesenheit)</td>
                      <td style={{ padding: '8px 12px', fontWeight: 700, color: 'var(--text-2)', fontSize: 15 }}>{fmt(data.gesamt_stunden)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Material — G.1: 3 gruppierte Tabellen statt einer flachen.
              Hauptauftrag und Nachträge mit Einheitspreis/Wert (LV-FK
              vorhanden), nicht zugeordnete Buchungen ohne Wert (kein
              LV → keine seriöse Preisbasis).
              Summe-Wert-Gesamt als kleiner Block oben; gesamtprojekt-
              Aggregate bleiben bewusst der Projektfortschritt-Page
              vorbehalten. */}
          {(data.material?.hauptauftrag?.items?.length > 0
            || data.material?.nachtraege?.items?.length > 0
            || data.material?.frei?.items?.length > 0) && (
            <>
              {/* Material-Wert-Gesamt-Block */}
              {(parseFloat(data.material?.summe_wert_gesamt) || 0) > 0 && (
                <div className="card" style={{ marginBottom: 14 }}>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                    gap: 12,
                  }}>
                    <div>
                      <div style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
                        Material-Wert Hauptauftrag
                      </div>
                      <div className="text-mono" style={{ fontSize: 18, fontWeight: 600, color: 'var(--copper)' }}>
                        {fmtEur(data.material.hauptauftrag?.summe_wert)}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
                        Material-Wert Nachträge
                      </div>
                      <div className="text-mono" style={{ fontSize: 18, fontWeight: 600, color: 'var(--copper)' }}>
                        {fmtEur(data.material.nachtraege?.summe_wert)}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
                        Material-Wert Gesamt (LV)
                      </div>
                      <div className="text-mono" style={{ fontSize: 18, fontWeight: 700, color: 'var(--copper)' }}>
                        {fmtEur(data.material.summe_wert_gesamt)}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <MaterialGruppeTabelle
                titel="Material — Hauptauftrag"
                items={data.material.hauptauftrag?.items}
                summeWert={data.material.hauptauftrag?.summe_wert}
                onDrilldown={openLvDrill}
              />
              <MaterialGruppeTabelle
                titel="Material — Nachträge"
                items={data.material.nachtraege?.items}
                summeWert={data.material.nachtraege?.summe_wert}
                onDrilldown={openLvDrill}
              />
              <MaterialGruppeTabelle
                titel="Material — nicht zugeordnet"
                items={data.material.frei?.items}
                hideValueColumns
                hinweis="Buchungen aus Bautagebüchern ohne Verknüpfung zu einer LV-Position — werden hier nur mengenmäßig ausgewiesen, fließen nicht in die LV-Wertsumme ein."
                onDrilldown={openFreiDrill}
              />
            </>
          )}

          {data.material?.hauptauftrag?.items?.length === 0
            && data.material?.nachtraege?.items?.length === 0
            && data.material?.frei?.items?.length === 0
            && data.zeiteintraege?.length === 0
            && (data.stunden_bautagebuch?.liste?.length || 0) === 0 && (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-3)' }}>
              Keine Daten für diesen Zeitraum gefunden.
            </div>
          )}
        </>
      )}

      {/* G.2: Drilldown-Modal — gleicher Component wie Projektfortschritt,
          mit Zeitraum-Kontext (von/bis) damit nur Verwendungen IM
          ausgewählten Zeitraum geladen werden. */}
      <VerwendungenModal
        trigger={drilldown}
        von={vonDatum}
        bis={bisDatum}
        onClose={closeDrill}
      />
    </div>
  );
}
